import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { LOGO_URL, BOOK_REVIEW_MAILTO } from "@/lib/brand";

const links = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/how-it-works", label: "How It Works" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/95 backdrop-blur-md shadow-[0_4px_24px_-12px_rgba(15,17,21,0.18)] border-b border-ink/10"
          : "bg-white border-b border-ink/5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-24 md:h-32 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo-link" className="flex items-center gap-3">
          <img
            src={LOGO_URL}
            alt="ETradingGroup"
            className="h-14 md:h-20 w-auto select-none"
            draggable={false}
          />
        </Link>

        <nav className="hidden md:flex items-center gap-9">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-[13px] tracking-[0.06em] font-medium link-reveal transition-colors ${
                  isActive ? "text-brand-red" : "text-ink/80 hover:text-ink"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <a
          href={BOOK_REVIEW_MAILTO}
          data-testid="nav-cta-book-review"
          className="hidden md:inline-flex items-center gap-2 bg-ink text-bg-base px-5 py-3 text-[12.5px] font-medium tracking-[0.04em] hover:bg-brand-red transition-colors"
        >
          Book a Review
          <span aria-hidden>→</span>
        </a>

        <button
          aria-label="Toggle menu"
          data-testid="nav-mobile-toggle"
          className="md:hidden p-2 -mr-2 text-ink"
          onClick={() => setOpen((s) => !s)}
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile drawer */}
      <div
        className={`md:hidden overflow-hidden border-t border-ink/10 bg-bg-base transition-[max-height] duration-500 ${
          open ? "max-h-[60vh]" : "max-h-0"
        }`}
      >
        <div className="px-6 py-6 flex flex-col gap-5">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`mobile-nav-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-2xl font-serif-display tracking-tight ${
                  isActive ? "text-brand-red" : "text-ink"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <a
            href={BOOK_REVIEW_MAILTO}
            data-testid="mobile-cta-book-review"
            className="mt-2 inline-flex items-center justify-center bg-ink text-bg-base px-5 py-4 font-medium"
          >
            Book a Review
          </a>
        </div>
      </div>
    </header>
  );
}
